import {Flag, Parameters, Rank, Tag, Tooltip} from 'platform/components';
import {Box, HStack, Icon, Integer, Show, Text, VStack} from 'platform/foundation';
import {NumberFormatter} from 'platform/locale';

import {ReactElement} from 'react';

import {isNotNil} from 'ramda';
import {floor} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {buildArray, EMPTY_PLACEHOLDER, isNotNilOrEmpty} from 'shared/web';

import {AuctionCountdownCell} from '../components/AuctionPreview/components/AuctionCountdownCell';
import {Clamp} from '../components/Clamp/Clamp';
import {HitlistBlacklistFlag} from '../components/HitlistBlacklistFlag/HitlistBlacklistFlag';
import {HeaderCellTooltip} from '../components/SourcingTable/components/HeaderCellTooltip';
import {Column} from '../components/SourcingTable/utils/getSourcingTableColumns';
import {AuctionEnumLookup} from '../hooks/useAuctionEnumLookup';
import {AuctionTypeFilter} from '../pages/AllOffers/types/AuctionTypeFilter';
import {AuctionSourcingListItem} from './adaptAuctionVehicleToSourcingItem';
import {buildAuctionVehicleTitle} from './buildAuctionVehicleTitle';
import {decimalToPercentRank} from './decimalToPercentRank';
import {resolveAuctionEnumLabel} from './resolveAuctionEnumLabel';

/**
 * Cell props for the auction table — mirrors the CarsCell shape but with
 * AuctionSourcingListItem as the row data type.
 */
type AuctionCell = {
  row: {
    original: AuctionSourcingListItem;
  };
};

/**
 * Type guard that narrows a CarsCell to AuctionCell.
 *
 * Auction rows are passed through the adaptAuctionVehicleToSourcingItem adapter in AllOffersPage.
 * `auctionVehicle.auctionId` is unique to auction rows and absent from SourcingVehicleListItemResponseBody,
 * so its presence is a reliable discriminant.
 */
function isAuctionCell(props: {row: {original: object}}): props is AuctionCell {
  return 'auctionVehicle' in props.row.original;
}

const cell =
  (render: (props: AuctionCell) => ReactElement): Column['Cell'] =>
  (props) => {
    if (isAuctionCell(props)) {
      return render(props);
    }

    return <></>;
  };

export type GetAuctionTableColumnsParams = {
  formatNumber: NumberFormatter;
  enumLookup: AuctionEnumLookup;
};

export function getAuctionTableColumns(params: GetAuctionTableColumnsParams): Column[] {
  return buildArray<Column>()
    .add({
      id: 'MAKE_MODEL_VARIANT_SPEC',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipMakeModelVariant')}>
          <Text size="xxSmall" color="secondary">
            {i18n.t('entity.vehicle.labels.makeModelVariantSpec')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const vehicle = row.original.auctionVehicle;
        const sourcingVehicle = row.original.sourcingVehicle;
        const isDisabled = sourcingVehicle?.isAvailable === false;
        const suspiciousReasons = sourcingVehicle?.suspiciousReason
          .map((reason) => i18n.t(`entity.vehicle.labels.${reason}`))
          .join(', ');

        const resolve = (value: string | null | undefined) =>
          resolveAuctionEnumLabel(value, params.enumLookup);

        const firstLine = buildAuctionVehicleTitle(
          vehicle.make,
          vehicle.model,
          vehicle.adTitle,
          params.enumLookup
        );

        const secondLineParams = buildArray<string>()
          .when(isNotNilOrEmpty(vehicle.seller?.name), vehicle.seller?.name ?? '')
          .when(isNotNilOrEmpty(vehicle.year), String(vehicle.year))
          .when((vehicle.power ?? 0) > 0, `${vehicle.power} kW`)
          .when(isNotNilOrEmpty(vehicle.transmission), resolve(vehicle.transmission) ?? '')
          .when(isNotNilOrEmpty(vehicle.driveType), resolve(vehicle.driveType) ?? '')
          .when(isNotNilOrEmpty(vehicle.fuelType), resolve(vehicle.fuelType) ?? '')
          .when(isNotNilOrEmpty(vehicle.carStyle), resolve(vehicle.carStyle) ?? '')
          .when(
            isNotNilOrEmpty(vehicle.mileage),
            `${params.formatNumber(vehicle.mileage ?? 0, 0)} km`
          );

        return (
          <VStack spacing={1}>
            <HStack spacing={1} align="center">
              <Show when={!!row.original.isNew}>
                <Tooltip label={i18n.t('entity.vehicle.labels.unseen')}>
                  <Box
                    backgroundColor="palettes.green.60.100"
                    width={3}
                    height={3}
                    borderRadius="circular"
                  />
                </Tooltip>
              </Show>
              <Show when={isDisabled}>
                <Flag label={i18n.t('entity.vehicle.labels.sold')} size="small" />
              </Show>
              <Tooltip label={firstLine}>
                <Clamp lines={1}>
                  <Text size="small" alternative>
                    {firstLine}
                  </Text>
                </Clamp>
              </Tooltip>
              <Show when={isNotNilOrEmpty(row.original.vehicleSummary?.comment)}>
                <Tooltip label={row.original.vehicleSummary?.comment}>
                  <Icon value="communication/comment" size={5} />
                </Tooltip>
              </Show>
              <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
              </Show>
              <Show
                whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                when={sourcingVehicle.suspicious}
              >
                <Tooltip label={suspiciousReasons}>
                  <Icon value="alert/warning" color="severity.warning" size={5} />
                </Tooltip>
              </Show>
            </HStack>
            <Parameters
              size="xSmall"
              color="secondary"
              parameters={secondLineParams}
              data-testid={testIds.sourcing.auctions('vehicleInfo-parameters')}
            />
          </VStack>
        );
      }),
    })
    .add({
      id: 'CURRENT_BID',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.auction.labels.currentBid')}>
          <Text size="xxSmall" color="secondary" align="right">
            {i18n.t('entity.auction.labels.currentBid')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const originalPrice = row.original.auctionVehicle.originalPriceActual;
        const currentBid = row.original.auctionVehicle.priceActual;
        const buyNowPrice = row.original.auctionVehicle.priceBuyNow;
        const auctionType = row.original.auctionVehicle.auctionType?.toLowerCase();
        const isCurrentBidAvailable = isNotNil(currentBid) && currentBid > 0;
        const isBuyNowType = auctionType === AuctionTypeFilter.BUY_NOW;
        const isOpenType = auctionType === AuctionTypeFilter.OPEN;
        const isClosedType = auctionType === AuctionTypeFilter.CLOSED;
        const isBuyNowPriceAvailable = isBuyNowType && isNotNil(buyNowPrice) && buyNowPrice > 0;
        const formattedBuyNowPrice = params.formatNumber(buyNowPrice ?? 0, 0);
        const originalPriceLabel = params.formatNumber(originalPrice ?? 0, 0);

        return (
          <VStack spacing={1} align="flex-end">
            <Show when={isCurrentBidAvailable}>
              <Tooltip label={originalPriceLabel}>
                <Text size="xSmall" alternative align="right">
                  {params.formatNumber(currentBid ?? 0, 0)}
                </Text>
              </Tooltip>
            </Show>
            <Show when={!isCurrentBidAvailable}>
              <Text size="xSmall" alternative color="secondary" align="right">
                {EMPTY_PLACEHOLDER}
              </Text>
            </Show>
            <Show when={isBuyNowPriceAvailable}>
              <Tooltip
                label={i18n.t('entity.auction.labels.buyNowForPrice', {
                  price: formattedBuyNowPrice,
                })}
              >
                <Box
                  backgroundColor="severity.success"
                  borderRadius="xSmall"
                  paddingHorizontal={1}
                  data-testid={testIds.sourcing.auctions('currentBid-buyNowFlag')}
                >
                  <HStack spacing={1} align="center">
                    <Icon value="action/shopping_cart" color="general.white" size={3} />
                    <Text size="xxSmall" color="white" noWrap>
                      {formattedBuyNowPrice}
                    </Text>
                  </HStack>
                </Box>
              </Tooltip>
            </Show>
            <Show when={isOpenType}>
              <Tag
                size="small"
                colorScheme="blue"
                label={i18n.t('entity.auction.labels.open')}
                data-testid={testIds.sourcing.auctions('currentBid-openFlag')}
              />
            </Show>
            <Show when={isClosedType}>
              <Tag
                size="small"
                colorScheme="neutral"
                label={i18n.t('entity.auction.labels.closed')}
                data-testid={testIds.sourcing.auctions('currentBid-closedFlag')}
              />
            </Show>
          </VStack>
        );
      }),
      width: 18 as Integer,
    })
    .add({
      id: 'MARGIN',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.margin')}>
          <Text size="xxSmall" color="secondary" align="right">
            {i18n.t('entity.vehicle.labels.margin')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const marginValue = row.original.auctionVehicle.maximalPossibleMargin;
        const vehicleCosts = row.original.summary?.vehicleCosts;
        const marginWithoutCost = params.formatNumber((marginValue ?? 0) - (vehicleCosts ?? 0), 0);
        const formattedMargin = isNotNil(marginValue)
          ? params.formatNumber(marginValue, 0)
          : EMPTY_PLACEHOLDER;

        return (
          <Tooltip
            label={i18n.t('entity.vehicle.labels.marginWithoutTotalCosts', {
              marginWithoutCost,
            })}
          >
            <Text
              size="xSmall"
              align="right"
              color={isNotNil(marginValue) && marginValue < 0 ? 'danger' : undefined}
            >
              {formattedMargin}
            </Text>
          </Tooltip>
        );
      }),
      width: 16 as Integer,
    })
    .add({
      id: 'RECOMMEND',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipRecommendedPrice')}>
          <Text size="xxSmall" color="secondary" align="right">
            {i18n.t('entity.vehicle.labels.recommendedPrice')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const predictedPrice = row.original.auctionVehicle.predictedPrice;
        const formattedPredictedPrice = isNotNil(predictedPrice)
          ? params.formatNumber(predictedPrice, 0)
          : EMPTY_PLACEHOLDER;

        return (
          <Text size="xSmall" align="right">
            {formattedPredictedPrice}
          </Text>
        );
      }),
      width: 16 as Integer,
    })
    .add({
      id: 'POSITION',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipPosition')}>
          <Text size="xxSmall" color="secondary" align="center">
            {i18n.t('entity.vehicle.labels.position')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const marketPosition = row.original.auctionVehicle.marketPosition;
        const marketPositionCount = row.original.auctionVehicle.marketPositionCount;

        const positionText =
          isNotNil(marketPosition) && isNotNil(marketPositionCount)
            ? `${marketPosition}/${marketPositionCount}`
            : EMPTY_PLACEHOLDER;

        const positionPercentage =
          isNotNil(marketPosition) && isNotNil(marketPositionCount) && marketPositionCount > 0
            ? Math.round(((marketPositionCount - marketPosition) / marketPositionCount) * 100)
            : null;

        const percentText = isNotNil(positionPercentage) ? `${positionPercentage}%` : undefined;

        return (
          <VStack align="center">
            <Text size="xSmall" noWrap>
              {positionText}
            </Text>
            <Show when={isNotNil(percentText)}>
              <Text size="xSmall" color="secondary">
                {percentText}
              </Text>
            </Show>
          </VStack>
        );
      }),
      width: 12 as Integer,
    })
    .add({
      id: 'LID',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipLID')}>
          <Text size="xxSmall" color="secondary">
            {i18n.t('entity.vehicle.labels.LID')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => (
        <Text size="xSmall">
          {isNotNil(row.original.auctionVehicle.turnover)
            ? params.formatNumber(row.original.auctionVehicle.turnover, 0)
            : EMPTY_PLACEHOLDER}
        </Text>
      )),
      width: 8 as Integer,
    })
    .add({
      id: 'FEATURES',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipRank')}>
          <Text size="xxSmall" color="secondary">
            {i18n.t('entity.vehicle.labels.features')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => (
        <Tooltip
          label={decimalToPercentRank(row.original.auctionVehicle.featureScore ?? undefined)}
        >
          <Rank
            percentage={floor((row.original.auctionVehicle.featureScore ?? 0) * 100)}
            data-testid={testIds.sourcing.auctions('rank-tooltip')}
          />
        </Tooltip>
      )),
      width: 10 as Integer,
    })
    .add({
      id: 'AUCTION_END',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.auction.labels.auctionEnds')}>
          <Text size="xxSmall" color="secondary">
            {i18n.t('entity.auction.labels.auctionEnd')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => (
        <AuctionCountdownCell endTime={row.original.auctionVehicle.carEndTime ?? ''} />
      )),
      width: 24 as Integer,
    });
}
